import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isApiError } from "@/api/client";
import { qk } from "@/api/queryKeys";
import { api, call, xClient } from "@/api/spec";
import type { ConsentCode, ConsentsResponse } from "@/api/types";
import { t, type MessageKey } from "@/i18n/ko";
import { formatDateDot } from "@/lib/formatDate";
import { useUiStore } from "@/stores/uiStore";

/** 설정 "알림" 섹션의 선택 동의 토글(code → 처리 결과 통지 문구). 웹·앱 공통, 계정 단위 */
const RESULT_KEYS: Partial<Record<ConsentCode, { on: MessageKey; off: MessageKey }>> = {
  marketing: { on: "settings.consent.marketing.agreed", off: "settings.consent.marketing.rejected" },
  night_marketing: { on: "settings.consent.nightMarketing.agreed", off: "settings.consent.nightMarketing.rejected" },
};

/**
 * 선택 동의 상태(GET /me/consents) + 변경(PUT /me/consents/{code}). 성공하면 서버 처리 시각으로
 * "함락 · {yyyy.mm.dd} … 동의했어요/거부했어요" 통지(정보통신망법 50조, 시안 595:6547).
 */
export function useConsentSetting(enabled: boolean) {
  const qc = useQueryClient();
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const query = useQuery({
    queryKey: qk.consents,
    queryFn: () => call(api.GET("/me/consents", { params: { header: xClient() } })),
    enabled,
  });
  const change = useMutation({
    mutationFn: ({ code, agreed }: { code: ConsentCode; agreed: boolean }) =>
      call(api.PUT("/me/consents/{code}", { params: { header: xClient(), path: { code } }, body: { agreed } })),
    onSuccess: (res) => {
      qc.setQueryData<ConsentsResponse>(qk.consents, (prev) => ({
        consents: [...(prev?.consents ?? []).filter((c) => c.code !== res.code), res],
      }));
      const keys = RESULT_KEYS[res.code];
      if (keys && res.agreedAt) showSnackbar(t(res.agreed ? keys.on : keys.off, { date: formatDateDot(res.agreedAt) }), undefined, "positive");
    },
    onError: (e) => showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("chat.settings.failed")),
  });
  const agreed = (code: ConsentCode) => query.data?.consents.find((c) => c.code === code)?.agreed ?? false;
  return {
    /** 상태를 받아 오기 전에는 토글을 숨긴다(꺼짐으로 잘못 보이지 않게) */
    ready: query.isSuccess,
    agreed,
    pending: change.isPending,
    toggle: (code: ConsentCode, next: boolean) => change.mutate({ code, agreed: next }),
  };
}
